<template>
  <!-- ຮັບຊຳລະໜີ້ — the shop paid its advance debt back itself (transfer / cash,
       outside COD). Money RECEIVED, unlike ຕັດໜີ້; the slip is the evidence.
       The dialog owns the title. -->
  <div class="repay">
    <div class="who">
      <span class="avatar is-lg">{{ initialOf(row.shop.name) }}</span>
      <span class="person-text">
        <b class="person-name">{{ row.shop.name }}</b>
        <span class="person-tel">{{ row.customer.name }} · {{ row.customer.tel }}</span>
      </span>
      <span class="who-owing">
        <span>ຄ້າງຊຳລະ</span>
        <b>{{ formatKip(row.outstanding) }}</b>
      </span>
    </div>

    <p class="info-note">
      <i class="fal fa-info-circle"></i>
      ບັນທຶກເງິນທີ່ຮ້ານໂອນ ຫຼື ຈ່າຍຄືນເອງ ນອກລະບົບ COD, 1 ການໂອນ = 1 ລາຍການ. ລະບົບຈະຫັກໃຫ້ລາຍການຖອນທີ່ເກົ່າສຸດກ່ອນ.
      ຍອດຄ້າງຊຳລະຈະຫຼຸດລົງ ແລະ ຍົກເລີກຄືນບໍ່ໄດ້
    </p>

    <div class="fld">
      <label class="fld-label">ຈຳນວນເງິນທີ່ໄດ້ຮັບ <i class="req">*</i></label>
      <KipInput v-model="form.amount" :max="owed" :disabled="saving || !advances.length"/>
      <p class="fld-hint" :class="{'is-bad': amountTooBig}" v-if="advances.length">
        {{ amountTooBig
          ? 'ຫຼາຍກວ່າຍອດທີ່ຮ້ານຄ້າງຊຳລະ ' + formatKip(owed)
          : 'ສູງສຸດ ' + formatKip(owed) + ' (ຍອດຄ້າງທັງໝົດ)' }}
      </p>
    </div>

    <!-- Where the payment goes: the open advances oldest first, as the server
         applies it. Display only — the server allocates. -->
    <div class="fld">
      <label class="fld-label">ລາຍການທີ່ຈະຫັກ (ເກົ່າສຸດກ່ອນ)</label>
      <p class="fld-hint" v-if="advancesLoading">ກຳລັງໂຫລດ...</p>
      <p class="fld-hint is-bad" v-else-if="advancesError">{{ advancesError }}</p>
      <p class="fld-hint" v-else-if="!advances.length">ບໍ່ມີລາຍການທີ່ຄ້າງຊຳລະ</p>
      <div class="alloc" v-else>
        <div class="alloc-row" v-for="a in allocation" :key="a.id" :class="{'is-untouched': !a.take}">
          <span class="alloc-ref">
            <b>{{ a.invoice_number || '#' + a.id }}</b>
            <small>{{ formatDateTime(a.requested_at) }} · ຄ້າງ {{ formatKip(a.outstanding) }}</small>
          </span>
          <span class="alloc-take">
            <template v-if="a.take">
              −{{ formatKip(a.take) }}
              <small>{{ a.take >= a.outstanding ? 'ຊຳລະຄົບ' : 'ເຫຼືອ ' + formatKip(a.outstanding - a.take) }}</small>
            </template>
            <small v-else>ບໍ່ຖືກຫັກ</small>
          </span>
        </div>
      </div>
    </div>

    <div class="fld">
      <label class="fld-label" for="cod-repay-date">ວັນທີທີ່ຮ້ານຊຳລະ <i class="req">*</i></label>
      <input id="cod-repay-date" class="fld-input" type="date" v-model="form.paid_at" :max="today" :disabled="saving">
    </div>

    <div class="fld">
      <label class="fld-label">ຮູບສລິບ / ຫຼັກຖານການຊຳລະ <i class="req">*</i></label>
      <input ref="file" class="is-hidden-input" type="file" accept=".jpg,.jpeg,.png" @change="onFile">
      <div class="dropzone" v-if="!slip" :class="{'is-over': dragOver}"
           @click="$refs.file.click()" @dragover.prevent="dragOver = true"
           @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
        <i class="fal fa-cloud-upload dz-icon"></i>
        <span class="dz-text is-plain">ລາກຮູບໃສ່ນີ້ ຫຼື <u>ເລືອກໄຟລ໌</u></span>
        <span class="dz-hint">PNG, JPG · ສູງສຸດ 10MB</span>
      </div>
      <div class="file-picked" v-else>
        <img class="file-thumb" v-if="slipPreview" :src="slipPreview" alt="">
        <span class="picked-name">{{ slip.name }}</span>
        <span class="picked-size">{{ fileSize(slip.size) }}</span>
        <button class="chosen-x" type="button" @click="removeFile" aria-label="ລຶບ">
          <i class="fal fa-times"></i>
        </button>
      </div>
    </div>

    <div class="fld">
      <label class="fld-label" for="cod-repay-note">ໝາຍເຫດ</label>
      <textarea id="cod-repay-note" class="fld-input" rows="2" v-model="form.note" maxlength="500" :disabled="saving"
                placeholder="ເຊັ່ນ: ໂອນເຂົ້າບັນຊີ BCEL ຂອງບໍລິສັດ"></textarea>
    </div>

    <!-- The same slip was already used for a repayment (this shop or another),
         or the same amount on the same payment day is already recorded for this
         shop: saved only when Finance says it is another payment. -->
    <div class="dup-warning" v-if="duplicate">
      <p>
        <i class="fal fa-exclamation-triangle"></i>
        {{ duplicate.message }}
      </p>
      <p class="dup-detail" v-if="duplicate.slipReused && duplicate.detail && !duplicate.detail.same_shop">
        ສະລິບນີ້ຖືກໃຊ້ກັບຮ້ານອື່ນ: {{ duplicate.detail.shop_name }}
      </p>
      <label class="dup-confirm">
        <input type="checkbox" v-model="confirmDuplicate">
        ແມ່ນການຊຳລະອີກຄັ້ງ ບໍ່ແມ່ນລາຍການຊ້ຳ
      </label>
    </div>

    <p class="form-error" v-if="error">{{ error }}</p>

    <div class="modal-actions is-split">
      <button class="cod-btn is-ghost" type="button" :disabled="saving" @click="$emit('close')">ຍົກເລີກ</button>
      <button class="cod-btn" type="button" :disabled="saving || !canSubmit" @click="submit">
        {{ saving ? 'ກຳລັງບັນທຶກ...' : 'ຢືນຢັນຮັບຊຳລະ' }}
      </button>
    </div>
  </div>
</template>

<script>
import KipInput from '@/views/Accounting/Customer/CodAdvance/components/KipInput';
import {errorMessage, fetchOpenAdvances, formatDateTime, formatKip, repayAdvance} from '@/services/codAdvanceService';

const MAX_SLIP_BYTES = 10 * 1024 * 1024;

// Saved only once Finance confirms it is another payment: the same slip
// already used (any shop), or the same amount on the same payment day.
const DUPLICATE_CODES = ['cod_advance_repayment_slip_reused', 'cod_advance_repayment_possible_duplicate'];

function isoToday() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

export default {
  name: 'RepaymentModal',
  components: {KipInput},
  props: {
    // An audit row: {shop: {name}, customer: {id, name, tel}, outstanding}.
    row: {type: Object, required: true},
  },
  data() {
    return {
      openedAt: Date.now(),
      today: isoToday(),
      form: {amount: null, paid_at: isoToday(), note: ''},
      advances: [],
      advancesLoading: false,
      advancesError: null,
      slip: null,
      slipPreview: null,
      dragOver: false,
      duplicate: null,
      confirmDuplicate: false,
      error: null,
      saving: false,
    };
  },
  computed: {
    // Everything the shop owes: the payment may not exceed it (the ledger refuses more).
    owed() {
      return this.advances.reduce((sum, a) => sum + (Number(a.outstanding) || 0), 0);
    },
    amountTooBig() {
      return this.form.amount > this.owed;
    },
    // How the typed amount lands, oldest advance first — the server's own
    // order (disbursed_at, id), so the preview matches what it records.
    allocation() {
      let left = Number(this.form.amount) || 0;
      return this.advances.map((a) => {
        const owing = Number(a.outstanding) || 0;
        const take = Math.max(0, Math.min(owing, left));
        left -= take;
        return {...a, take};
      });
    },
    canSubmit() {
      return this.advances.length > 0
        && this.form.amount > 0
        && !this.amountTooBig
        && !!this.form.paid_at
        && this.form.paid_at <= this.today
        && !!this.slip
        && (!this.duplicate || (this.confirmDuplicate && this.duplicate.key === this.entryKey()));
    },
  },
  watch: {
    // The dialog hides its ✕ while a save is in flight (`busy`).
    saving(value) { this.$emit('busy', value); },
    // A changed entry is no longer the one the duplicate warning was about.
    'form.amount'() { this.duplicate = null; this.confirmDuplicate = false; },
    'form.paid_at'() { this.duplicate = null; this.confirmDuplicate = false; },
    slip() { this.duplicate = null; this.confirmDuplicate = false; },
  },
  created() {
    this.loadAdvances();
  },
  beforeDestroy() {
    this.revokePreview();
  },
  methods: {
    formatKip,
    formatDateTime,
    initialOf(name) {
      return name ? String(name).trim().charAt(0).toUpperCase() : '?';
    },
    fileSize(bytes) {
      const kb = (Number(bytes) || 0) / 1024;
      return kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : Math.round(kb) + ' KB';
    },
    async loadAdvances() {
      this.advancesLoading = true;
      this.advancesError = null;
      try {
        this.advances = await fetchOpenAdvances(this.row.customer.id);
        // Most payments clear the whole debt: start from it.
        this.form.amount = this.advances.length ? this.owed : null;
      } catch (e) {
        this.advancesError = errorMessage(e, 'ໂຫລດລາຍການຖອນບໍ່ສຳເລັດ');
      } finally {
        this.advancesLoading = false;
      }
    },
    onFile(event) {
      this.acceptFile(event.target.files[0] || null);
    },
    onDrop(event) {
      this.dragOver = false;
      this.acceptFile(event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null);
    },
    acceptFile(file) {
      if (!file) return;
      if (!/\.(jpe?g|png)$/i.test(file.name)) {
        this.error = 'ຮັບສະເພາະ PNG ຫຼື JPG';
        return;
      }
      if (file.size > MAX_SLIP_BYTES) {
        this.error = 'ຟາຍໃຫຍ່ເກີນ 10 MB';
        return;
      }
      this.error = null;
      this.revokePreview();
      this.slip = file;
      this.slipPreview = URL.createObjectURL(file);
    },
    removeFile() {
      this.revokePreview();
      this.slip = null;
      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    },
    revokePreview() {
      if (this.slipPreview) {
        URL.revokeObjectURL(this.slipPreview);
        this.slipPreview = null;
      }
    },
    slipIdentity() {
      return this.slip ? [this.slip.name, this.slip.size, this.slip.lastModified].join(':') : '';
    },
    // What a duplicate warning is about: the amount, payment day and slip file.
    entryKey() {
      return [this.form.amount, this.form.paid_at, this.slipIdentity()].join('|');
    },
    // Derived from the PAYLOAD, slip included (the server binds the key to the
    // file too): the same entry retried after a lost answer is the same key
    // (the server returns the first, stores no second slip); a corrected entry
    // — another slip included — is a new key, which the duplicate checks then
    // catch if the first did save. The server refuses a key reused for
    // different details.
    requestId() {
      const payload = [this.form.amount, this.form.paid_at, (this.form.note || '').trim(), this.slipIdentity()].join('|');
      let hash = 0;
      for (let i = 0; i < payload.length; i += 1) {
        hash = ((hash << 5) - hash + payload.charCodeAt(i)) | 0;
      }
      return 'RP-' + this.row.customer.id + '-' + this.openedAt + '-' + (hash >>> 0).toString(36);
    },
    async submit() {
      if (this.saving) return;
      this.error = null;
      if (!this.canSubmit) {
        this.error = 'ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບ';
        return;
      }
      this.saving = true;
      const submittedKey = this.entryKey();
      try {
        const data = new FormData();
        data.append('request_id', this.requestId());
        data.append('amount', this.form.amount);
        data.append('paid_at', this.form.paid_at);
        data.append('slip', this.slip);
        if ((this.form.note || '').trim()) {
          data.append('note', this.form.note.trim());
        }
        if (this.duplicate && this.confirmDuplicate && this.duplicate.key === submittedKey) {
          data.append('confirm_duplicate', '1');
        }
        this.$emit('done', await repayAdvance(this.row.customer.id, data));
      } catch (e) {
        const body = e && e.response ? e.response.data : null;
        if (body && DUPLICATE_CODES.includes(body.code)) {
          // Only for the details that were sent — a late answer about an entry
          // since edited must not be confirmable for the new one.
          if (submittedKey === this.entryKey()) {
            this.duplicate = {
              message: body.message,
              detail: body.duplicate || null,
              slipReused: body.code === 'cod_advance_repayment_slip_reused',
              key: submittedKey,
            };
            this.confirmDuplicate = false;
          }
        } else {
          this.error = errorMessage(e, 'ບັນທຶກການຊຳລະບໍ່ສຳເລັດ');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.repay {
  text-align: left;
  width: 460px;
  max-width: 100%;
  color: $cod-ink;
}

.who {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid $cod-line;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #fafbfc;
}

.who-owing {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  span {
    font-size: 11px;
    color: $cod-muted;
  }

  b {
    color: $cod-danger;
  }
}

.info-note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #e8f1fd;
  border: 1px solid #cfe0fb;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  color: $cod-blue;
  margin-bottom: 16px;
}

.dropzone .dz-text.is-plain {
  color: $cod-ink;

  u {
    color: $cod-blue;
  }
}

.fld-hint.is-bad {
  color: $cod-danger;
}

.dup-warning {
  background: #fff7e6;
  border: 1px solid #ffe0a3;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  color: #8a5a00;
  margin-bottom: 12px;

  p {
    display: flex;
    gap: 8px;
    margin: 0 0 8px;
  }
}

.dup-detail {
  font-weight: 600;
}

.alloc {
  border: 1px solid $cod-line;
  border-radius: 10px;
  padding: 2px 12px;
}

.alloc-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f2f4f7;
  font-size: 13px;

  &:last-child {
    border-bottom: 0;
  }

  &.is-untouched {
    color: $cod-muted;
  }

  small {
    display: block;
    color: $cod-muted;
    font-size: 11px;
  }
}

.alloc-take {
  text-align: right;
  white-space: nowrap;
  color: $cod-blue;
  font-weight: 600;
}

.dup-confirm {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  cursor: pointer;
}

// Bulma sets a system font on form controls; keep the app's (Lao) face.
button,
input,
select,
textarea {
  font-family: inherit;
}
</style>
